import React, { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowAltCircleLeft,
  faEyeDropper,
} from "@fortawesome/free-solid-svg-icons";
import ColorPicker from "../../../Component/ColorPicker/ColorPicker";
import Modal from "../../../Component/Modal/Modal";
import IframeCode from "./IframeCode";
import RightModal from "./RightModal";
import FormField from "./FormField";
import Tuto from "../../../asset/tuto.png";

const CreateWidget = ({ userId }) => {
  const user = useSelector((state) => state.userReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [widgetId, setWidgetId] = useState("");
  const [formChanged, setFormChanged] = useState(false);

  const [widgetData, setWidgetData] = useState({
    userId: userId,
    title: "",
    pricePerKmDay: "",
    pricePerKmNight: "",
    pricePerKmSunday: 0,
    pricePerKmRoundTripDay: "",
    pricePerKmRoundTripSunday: "",
    pricePerPassenger: "",
    pricePerBaggage: "",
    pricePriseEnCharge: "",
    flatFareEnabled: false,
    flatFareKmThreshold: "",
    flatFareAmount: "",
    flatFareBeyondKmPrice: "",
    contactMail: "",
    contactPhone: "",
    isOnline: "online",
    type: "Widget Taxi",
    btnColor: "",
    backgroundColor: "",
    labelColor: "",
  });

  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [colorPickers, setColorPickers] = useState({
    backgroundColor: false,
    btnColor: false,
    labelColor: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWidgetData((prev) => ({ ...prev, [name]: value }));
    setFormChanged(true);
  };

  const handleModeChange = (flatFareEnabled) => {
    setWidgetData((prev) => ({ ...prev, flatFareEnabled }));
    setFormChanged(true);
  };

  const handleColorPickerToggle = (fieldName) => {
    setColorPickers((prev) =>
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = key === fieldName ? !prev[key] : false;
        return acc;
      }, {})
    );
    setActiveColorPicker(fieldName);
  };

  const handleColorChange = (color) => {
    setWidgetData((prev) => ({ ...prev, [activeColorPicker]: color.hex }));
    setFormChanged(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/widgets/taxi/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(widgetData),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success("Widget créé avec succès");
        setWidgetId(data.widget._id);
        setFormChanged(false);
      } else {
        switch (response.status) {
          case 401:
            toast.error("Vous n'êtes pas connecté");
            break;
          case 400:
            toast.error("Attention, un widget existe déjà avec ce nom.");
            break;
          default:
            toast.error("Une erreur est survenue");
        }
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Une erreur est survenue");
    }
    setIsLoading(false);
  };

  if (!user.user.isPremium) {
    return (
      <div className="p-4 w-full">
        <p className="text-gray-500 text-sm text-center mt-10">
          Vous n'êtes pas encore autorisé.
        </p>
      </div>
    );
  }

  const isFlat = widgetData.flatFareEnabled;

  return (
    <div className="p-4 w-full">
      <Link to={`/`}>
        <button className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 my-1 w-[200px] flex justify-center items-center gap-2 text-sm font-medium transition-colors">
          <FontAwesomeIcon icon={faArrowAltCircleLeft} />
          <p>Revenir à la liste</p>
        </button>
      </Link>

      <div className="max-w-2xl mx-auto mt-6">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* ── Section 1 : Informations générales ── */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Informations générales
            </p>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Titre du widget *
              </label>
              <input
                className="text-center block w-full rounded-lg border border-gray-200 p-2.5 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                type="text"
                name="title"
                placeholder="Ex : VTC Paris — Gare du Nord"
                value={widgetData.title}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* ── Section 2 : Tarification ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Tarification
            </p>

            <div className="inline-flex p-1 bg-white border border-gray-200 rounded-lg w-fit">
              <ModeButton
                active={!isFlat}
                onClick={() => handleModeChange(false)}
              >
                Au kilomètre
              </ModeButton>
              <ModeButton
                active={isFlat}
                onClick={() => handleModeChange(true)}
              >
                Forfait
              </ModeButton>
            </div>

            {!isFlat ? (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField
                    label="Aller simple — jour"
                    hint="7h–19h, lun.–sam."
                    name="pricePerKmDay"
                    suffix="€/km"
                    placeholder="0.00"
                    value={widgetData.pricePerKmDay}
                    onChange={handleInputChange}
                    required
                  />
                  <FormField
                    label="Aller simple — nuit"
                    hint="19h–7h + dim."
                    name="pricePerKmNight"
                    suffix="€/km"
                    placeholder="0.00"
                    value={widgetData.pricePerKmNight}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FormField
                      label="Aller-retour — jour"
                      hint="7h–19h, lun.–sam."
                      name="pricePerKmRoundTripDay"
                      suffix="€/km"
                      placeholder="0.00"
                      value={widgetData.pricePerKmRoundTripDay}
                      onChange={handleInputChange}
                    />
                    <FormField
                      label="Aller-retour — nuit"
                      hint="19h–7h + dim."
                      name="pricePerKmRoundTripSunday"
                      suffix="€/km"
                      placeholder="0.00"
                      value={widgetData.pricePerKmRoundTripSunday}
                      onChange={handleInputChange}
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    Optionnel · appliqué sur la distance aller + retour. Laissez
                    vide pour utiliser le tarif aller simple.
                  </p>
                </div>
                <FormField
                  label="Prise en charge"
                  hint="Ajoutée à chaque course"
                  name="pricePriseEnCharge"
                  suffix="€"
                  placeholder="0.00"
                  value={widgetData.pricePriseEnCharge}
                  onChange={handleInputChange}
                  required
                />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <FormField
                    label="Forfait"
                    name="flatFareAmount"
                    suffix="€"
                    placeholder="25"
                    value={widgetData.flatFareAmount}
                    onChange={handleInputChange}
                    required
                  />
                  <FormField
                    label="Jusqu'à"
                    name="flatFareKmThreshold"
                    suffix="km"
                    placeholder="10"
                    step="1"
                    value={widgetData.flatFareKmThreshold}
                    onChange={handleInputChange}
                    required
                  />
                  <FormField
                    label="Au-delà"
                    name="flatFareBeyondKmPrice"
                    suffix="€/km"
                    placeholder="2"
                    value={widgetData.flatFareBeyondKmPrice}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong className="text-amber-900">Aperçu :</strong>{" "}
                    jusqu'à{" "}
                    <strong>{widgetData.flatFareKmThreshold || "?"} km</strong>{" "}
                    ={" "}
                    <strong>{widgetData.flatFareAmount || "?"} €</strong> ·
                    au-delà ={" "}
                    <strong>
                      {widgetData.flatFareBeyondKmPrice || "?"} €/km
                    </strong>{" "}
                    (×2 si aller-retour)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── Section 3 : Suppléments ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Suppléments
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField
                label="Par passager"
                hint="Dès le 5e"
                name="pricePerPassenger"
                suffix="€"
                placeholder="0.00"
                value={widgetData.pricePerPassenger}
                onChange={handleInputChange}
                required
              />
              <FormField
                label="Par bagage"
                hint="Au-delà de 3"
                name="pricePerBaggage"
                suffix="€"
                placeholder="0.00"
                value={widgetData.pricePerBaggage}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* ── Section 4 : Apparence ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Apparence
            </p>
            <div className="-mx-3 flex flex-wrap">
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur de fond"
                  color={widgetData.backgroundColor}
                  onColorChange={(color) => handleColorChange(color)}
                  isOpen={colorPickers.backgroundColor}
                  togglePicker={() =>
                    handleColorPickerToggle("backgroundColor")
                  }
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur des boutons"
                  color={widgetData.btnColor}
                  onColorChange={(color) => handleColorChange(color)}
                  isOpen={colorPickers.btnColor}
                  togglePicker={() => handleColorPickerToggle("btnColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur du texte"
                  color={widgetData.labelColor}
                  onColorChange={(color) => handleColorChange(color)}
                  isOpen={colorPickers.labelColor}
                  togglePicker={() => handleColorPickerToggle("labelColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
            </div>
          </div>

          {/* ── Section 5 : Contact ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Contact
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Email *
                </label>
                <input
                  className="text-center block w-full rounded-lg border border-gray-200 p-2.5 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                  type="email"
                  name="contactMail"
                  placeholder="contact@exemple.fr"
                  value={widgetData.contactMail}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Téléphone *
                </label>
                <input
                  className="text-center block w-full rounded-lg border border-gray-200 p-2.5 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                  type="tel"
                  pattern="[0-9]{10}"
                  name="contactPhone"
                  placeholder="0612345678"
                  value={widgetData.contactPhone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* ── Submit ── */}
          <button
            type="submit"
            disabled={isLoading || !formChanged}
            className="w-full p-3 rounded-xl text-sm font-semibold text-white bg-black hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading
              ? "Création en cours…"
              : formChanged
              ? "Créer le widget"
              : "Compléter le formulaire"}
          </button>
        </form>

        <div className="tutoriel flex w-full mt-10 justify-center">
          <RightModal image={Tuto} tutoMode={true} />
        </div>
      </div>

      {widgetId && (
        <Modal
          title="Code iframe"
          isOpen={true}
          onClose={() => setWidgetId("")}
          children={<IframeCode widgetId={widgetId} />}
        />
      )}
    </div>
  );
};

const ModeButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={
      active
        ? "px-4 py-1.5 text-xs font-semibold rounded-md bg-black text-white transition-colors"
        : "px-4 py-1.5 text-xs font-semibold rounded-md text-gray-500 hover:text-gray-900 transition-colors"
    }
  >
    {children}
  </button>
);

export default CreateWidget;
